import { tableSortMixin } from "./tableSortMixin";

/**
 * Mixin base para estandarizar la lógica común de vistas CRUD:
 * - Ordenamiento de tablas (heredado de tableSortMixin)
 * - Paginación reactiva
 * - Estado de ventanas modales (alta, edición, toggle, cierre)
 * - Manejo y consulta de errores de validación del backend
 */
export const entityCrudMixin = {
  mixins: [tableSortMixin],

  data() {
    return {
      page: 1,
      post: false,
      showModal: false,
      operation: null,
      editData: null,
      errors: null,
    };
  },

  methods: {
    /**
     * Manejador estándar de cambio de página
     */
    changePage(page) {
      this.page = page;
      this.executeSearch(false);
    },

    /**
     * Manejador de ordenamiento por columna de tabla
     */
    orderByfield(field) {
      this.toggleOrder(field);
      this.executeSearch(false);
    },

    /**
     * Ejecuta el método de búsqueda/recarga definido en el componente consumidor
     * Busca por convención: 'buscar', 'fetchData' o métodos 'get*Busqueda'
     */
    executeSearch(hideResult = false) {
      if (typeof this.buscar === "function") {
        this.buscar(hideResult);
      } else if (typeof this.fetchData === "function") {
        this.fetchData(hideResult);
      } else if (typeof this.onSearch === "function") {
        this.onSearch(hideResult);
      }
    },

    /**
     * Abre el modal en el modo especificado ('add', 'edit', 'toggle', 'view', etc.)
     */
    showEdit(item = {}, operation = "edit") {
      this.errors = null;
      this.operation = operation;
      this.editData = Object.assign({}, item);
      this.showModal = true;
    },

    /**
     * Cierra el modal y resetea el estado
     */
    closeModal() {
      this.showModal = false;
      this.operation = null;
      this.editData = null;
      this.errors = null;
    },

    /**
     * Obtiene los errores de validación para un campo específico
     * @param {string} field
     * @returns {Array|null}
     */
    getError(field) {
      if (!this.errors) return null;
      if (typeof this.errors === "object" && this.errors[field]) {
        return Array.isArray(this.errors[field])
          ? this.errors[field]
          : [this.errors[field]];
      }
      return null;
    },

    /**
     * Indica si un campo específico tiene un error de validación activo
     * @param {string} field
     * @returns {boolean}
     */
    hasFieldError(field) {
      if (!this.errors || !field) return false;
      const aliases = {
        full_price: ["full_price", "precio_sin_imp"],
        precio_sin_imp: ["precio_sin_imp", "full_price"],
        full_price_tax: ["full_price_tax", "precio_con_imp"],
        precio_con_imp: ["precio_con_imp", "full_price_tax"],
        min_price_bs: ["min_price_bs", "precio_min"],
        precio_min: ["precio_min", "min_price_bs"],
        numero_corto: ["numero_corto", "numero"],
        numero: ["numero", "numero_corto"],
        ctr_url: ["ctr_url", "endpoint_eventos", "endpoint"],
      };
      const candidates = aliases[field] || [field];

      if (typeof this.errors === "object") {
        const errObj = this.errors.errors || this.errors;
        if (errObj && typeof errObj === "object" && !Array.isArray(errObj)) {
          for (const cand of candidates) {
            if (errObj[cand]) return true;
          }
        }
      }

      const errStr =
        typeof this.errors === "string"
          ? this.errors
          : (this.errors && (this.errors.error || this.errors.message)) || "";
      if (!errStr) return false;

      for (const cand of candidates) {
        if (
          errStr.includes(`Columna '${cand}'`) ||
          errStr.includes(`"${cand}"`)
        ) {
          return true;
        }
      }

      const fieldLabels = {
        id_integrador: ["integrador"],
        integrador: ["nombre del integrador", "integrador"],
        sequence: ["secuencia"],
        descripcion: ["descripción", "descripcion"],
        full_price: ["precio sin impuestos"],
        precio_sin_imp: ["precio sin impuestos"],
        full_price_tax: ["precio (con impuestos)", "precio con impuestos"],
        precio_con_imp: ["precio (con impuestos)", "precio con impuestos"],
        min_price_bs: ["precio mínimo", "precio minimo"],
        precio_min: ["precio mínimo", "precio minimo"],
        lifetime: ["lifetime"],
        frecuency: ["frecuencia"],
        bill_restriction: ["restricciones"],
        cod_tas: ["código tas", "codigo tas"],
        numero_corto: ["número corto", "numero corto"],
        numero: ["número corto", "numero corto"],
        keyword: ["keyword"],
        id_keyword: ["keyword"],
        producto: ["producto"],
        id_producto: ["producto"],
        alias: ["nombre (alias)", "alias"],
        id_categoria: ["categoría", "categoria"],
        id_operador: ["operador"],
        operador: ["operador"],
        id_tipo_suscripcion: ["tipo de suscripción", "tipo de suscripcion"],
        ctr_url: ["endpoint para ctrs"],
      };

      const lower = errStr.toLowerCase();
      for (const cand of candidates) {
        const labels = fieldLabels[cand] || [];
        for (const lbl of labels) {
          if (
            lower.includes(`el campo "${lbl}"`) ||
            lower.includes(`el campo '${lbl}'`) ||
            lower.includes(`${lbl}:`)
          ) {
            return true;
          }
        }
      }

      if (candidates.includes("id_integrador") && /seleccionar un integrador|seleccione un integrador/.test(lower)) {
        return true;
      }
      if (candidates.includes("id_categoria") && /seleccionar una categor[ií]a/.test(lower)) {
        return true;
      }
      if (candidates.includes("integrador") && (/nombre del integrador/.test(lower) || (/ese integrador\b/.test(lower) && !lower.includes(" y ")))) {
        return true;
      }
      if (candidates.includes("keyword") && /la keyword|y keyword\b|ese keyword\b/.test(lower)) {
        return true;
      }
      if (candidates.includes("cod_tas") && /cod\s*tas|c[oó]digo\s*tas/.test(lower)) {
        return true;
      }
      if (candidates.includes("numero_corto") && /numero\s*corto|n[uú]mero\s*corto/.test(lower)) {
        return true;
      }
      if (candidates.includes("sequence") && /secuencia|sequence/.test(lower) && !/seleccionar un integrador/.test(lower)) {
        return true;
      }
      return false;
    },

    /**
     * Formatea de manera amigable errores genéricos de clave duplicada o validación
     */
    cleanDuplicateError(err) {
      if (!err) return "";
      const errStr = String(err);
      if (errStr.includes("1062") || errStr.includes("Duplicate entry")) {
        const keyMatch = errStr.match(/for key ['"]([^'"]+)['"]/);
        if (keyMatch) {
          const rawKey = keyMatch[1].split(".").pop();
          if (rawKey.toUpperCase() === "PRIMARY") {
            return "Ya existe un registro con ese identificador.";
          }
          if (rawKey.startsWith("UNI_")) {
            const clean = rawKey.substring(4);
            const [table, colsStr] = clean.includes("__")
              ? clean.split("__")
              : [clean.split("_")[0], clean.split("_").slice(1).join("_")];
            const tableLabel = (table || "registro").replace(/_/g, " ").trim();
            const cols = (colsStr || "")
              .split("_")
              .filter((c) => c !== "id")
              .join(" y ");
            return `Ya existe un ${tableLabel} con ese ${cols}.`;
          }
        }
        return "Ya existe un registro con esos datos duplicados.";
      }
      return errStr;
    },
  },
};
